import { Input, Popover, PopoverSurface, PopoverTrigger, makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { Emoji16Regular } from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { FuiButton } from "@/components/fui-control";

const EMOJI_GROUPS: { label: string; items: string[] }[] = [
  { label: "자주", items: ["👍", "👎", "❤️", "😂", "😮", "😢", "🙏", "✅", "👀", "🔥", "🎉", "💡"] },
  { label: "표정", items: ["😀", "😃", "😄", "😁", "😊", "😉", "😍", "🤩", "😘", "😎", "🤔", "😐", "😴", "😭", "😤", "🤯"] },
  { label: "손짓", items: ["👋", "👏", "🙌", "🤝", "💪", "✌️", "🤞", "👌", "👉", "☝️"] },
  { label: "업무", items: ["📌", "📝", "📎", "🔗", "📅", "⏰", "📊", "📈", "🗂️", "💬", "🔔", "⚠️"] },
];

const useStyles = makeStyles({
  surface: {
    width: "220px",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(8, minmax(0, 1fr))",
    gap: "2px",
    maxHeight: "148px",
    overflowY: "auto",
  },
  cell: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    background: "transparent",
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    cursor: "pointer",
    fontSize: "16px",
    lineHeight: 1,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: tokens.colorStrokeFocus2,
    },
  },
  groupLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    padding: "2px 2px 0",
  },
});

export function CommentEmojiPicker({ onPick }: { onPick: (emoji: string) => void }) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const groups = useMemo(() => {
    const q = query.trim();
    if (!q) return EMOJI_GROUPS;
    return EMOJI_GROUPS
      .map((g) => ({ ...g, items: g.items.filter((e) => e.includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [query]);

  return (
    <Popover
      open={open}
      onOpenChange={(_, d) => {
        setOpen(d.open);
        if (!d.open) setQuery("");
      }}
      positioning="below-start"
    >
      <PopoverTrigger disableButtonEnhancement>
        <FuiButton appearance="subtle" density="dialog" icon={<Emoji16Regular />} aria-label="이모지 삽입" />
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <Input
          size="small"
          value={query}
          placeholder="이모지 검색"
          aria-label="이모지 검색"
          onChange={(_, d) => setQuery(d.value)}
        />
        {groups.map((group) => (
          <div key={group.label}>
            <div className={styles.groupLabel}>{group.label}</div>
            <div className={styles.grid}>
              {group.items.map((emoji) => (
                <button
                  key={`${group.label}-${emoji}`}
                  type="button"
                  className={styles.cell}
                  onClick={() => {
                    onPick(emoji);
                    setOpen(false);
                    setQuery("");
                  }}
                  aria-label={emoji}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        ))}
      </PopoverSurface>
    </Popover>
  );
}
